// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { PerInvoiceGantt } from '@/billing/components/lifecycle-per-invoice-gantt';
import { formatDays } from '@/billing/lifecycle/date-helpers';
import type { SegmentKey } from '@/billing/lifecycle/segments';
import { SEGMENTS } from '@/billing/lifecycle/segments';
import type {
  LifecycleCarryingCostBreakdown,
  LifecycleRow,
} from '@/billing/lifecycle/transforms';
import {
  KeyboardArrowDownRounded,
  KeyboardArrowRightRounded,
} from '@/constants/hardfin-standard-icons';
import { SortDirection } from '@/constants/sort';
import type { IsoCurrencyCode } from '@/currency/constants/iso';
import { formatAmountWithCurrency } from '@/currency/utilities/format';
import { useTableSort } from '@/hooks/use-table-sort';
import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link } from '@tanstack/react-router';
import { Decimal } from 'decimal.js';
import { Fragment, useState } from 'react';

const EM_DASH = '—';

// Column keys the backend accepts for sort — mirrors
// allowedListLifecycleColumns in data/requests/invoice/lifecycle.go.
// Adding a column on one side requires adding it on the other.
export type LifecycleSortColumn =
  | 'invoiceNumber'
  | 'customer'
  | 'project'
  | 'amount'
  | 'carryingCost'
  | SegmentKey;

interface LifecycleTableProps {
  data: LifecycleRow[];
  sortColumn: LifecycleSortColumn;
  sortDirection: SortDirection;
  onSort: (column: LifecycleSortColumn) => void;
  isLoading: boolean;
}

interface ColumnDef {
  key: LifecycleSortColumn | 'expander';
  label: string;
  align: 'left' | 'right' | 'center';
  minWidth?: number;
  sortable: boolean;
}

const TABLE_COLUMNS: ColumnDef[] = [
  {
    key: 'expander',
    label: '',
    align: 'center',
    minWidth: 40,
    sortable: false,
  },
  {
    key: 'invoiceNumber',
    label: 'Invoice #',
    align: 'left',
    minWidth: 140,
    sortable: true,
  },
  { key: 'customer', label: 'Customer', align: 'left', sortable: true },
  { key: 'project', label: 'Project', align: 'left', sortable: true },
  { key: 'amount', label: 'Amount', align: 'right', sortable: true },
  { key: 'chargeLag', label: 'Charge lag', align: 'right', sortable: true },
  { key: 'invoiceReview', label: 'Review', align: 'right', sortable: true },
  { key: 'deliveryLag', label: 'Delivery', align: 'right', sortable: true },
  { key: 'dateLag', label: 'Date lag', align: 'right', sortable: true },
  { key: 'actualTerms', label: 'Actual terms', align: 'right', sortable: true },
  { key: 'delinquency', label: 'Delinquency', align: 'right', sortable: true },
  { key: 'totalCycle', label: 'Total', align: 'right', sortable: true },
  {
    key: 'carryingCost',
    label: 'Carrying cost / gain',
    align: 'right',
    sortable: true,
  },
];

export function LifecycleTable(props: LifecycleTableProps) {
  const { data, sortColumn, sortDirection, onSort, isLoading } = props;
  // Session-only accordion state — which row's per-invoice Gantt is
  // currently expanded. Only one row open at a time.
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const toggleExpanded = (invoiceKey: string) => {
    setExpandedKey((prev) => (prev === invoiceKey ? null : invoiceKey));
  };

  const { getLabelDirection, isLabelActive } = useTableSort<
    LifecycleSortColumn | 'expander'
  >({ property: sortColumn, direction: sortDirection });

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table>
        <TableHead>
          <TableRow>
            {TABLE_COLUMNS.map((col) => (
              <TableCell
                key={col.key}
                align={col.align}
                sx={{ minWidth: col.minWidth }}
              >
                {col.sortable ? (
                  <TableSortLabel
                    active={isLabelActive(col.key)}
                    direction={getLabelDirection(col.key)}
                    onClick={() => onSort(col.key as LifecycleSortColumn)}
                  >
                    {col.label}
                  </TableSortLabel>
                ) : (
                  col.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading && <LifecycleTableSkeletonRows />}
          {!isLoading &&
            data.map((row) => {
              const isExpanded = expandedKey === row.invoiceKey;
              return (
                <Fragment key={row.invoiceKey}>
                  <TableRow hover>
                    <TableCell align="center" sx={{ width: 40, padding: 0 }}>
                      <IconButton
                        size="small"
                        aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                        onClick={() => toggleExpanded(row.invoiceKey)}
                      >
                        {isExpanded ? (
                          <KeyboardArrowDownRounded fontSize="small" />
                        ) : (
                          <KeyboardArrowRightRounded fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Link
                        to="/billing/invoices/$invoiceKey"
                        params={{ invoiceKey: row.invoiceKey }}
                        style={{ textDecoration: 'none' }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color="primary.main"
                          sx={{ cursor: 'pointer' }}
                        >
                          {row.invoiceNumber}
                        </Typography>
                      </Link>
                    </TableCell>
                    <TableCell>
                      {row.customer ?? (
                        <Typography variant="body2" color="text.disabled">
                          {EM_DASH}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.project ?? (
                        <Typography variant="body2" color="text.disabled">
                          {EM_DASH}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {formatAmountWithCurrency(row.amount, row.currency)}
                    </TableCell>
                    <DaysCell value={row.chargeLag} segmentKey="chargeLag" />
                    <DaysCell
                      value={row.invoiceReview}
                      segmentKey="invoiceReview"
                    />
                    <DaysCell
                      value={row.deliveryLag}
                      segmentKey="deliveryLag"
                    />
                    <DaysCell value={row.dateLag} segmentKey="dateLag" />
                    <DaysCell
                      value={row.actualTerms}
                      segmentKey="actualTerms"
                    />
                    <DaysCell
                      value={row.delinquency}
                      segmentKey="delinquency"
                    />
                    <DaysCell value={row.totalCycle} segmentKey="totalCycle" />
                    <CarryingCostCell
                      value={row.carryingCost}
                      breakdown={row.carryingCostBreakdown}
                      currencyCode={row.currency}
                    />
                  </TableRow>
                  <TableRow>
                    <TableCell
                      colSpan={TABLE_COLUMNS.length}
                      sx={{ padding: 0, borderBottom: 'none' }}
                    >
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box
                          sx={{ padding: 2, backgroundColor: 'action.hover' }}
                        >
                          <PerInvoiceGantt row={row} />
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              );
            })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function LifecycleTableSkeletonRows() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <TableRow key={i}>
          {TABLE_COLUMNS.map((col) => (
            <TableCell key={col.key} align={col.align}>
              <Skeleton variant="text" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

interface DaysCellProps {
  value: number | null;
  segmentKey: SegmentKey;
}

function DaysCell(props: DaysCellProps) {
  const { value, segmentKey } = props;
  if (value === null) {
    return (
      <TableCell align="right">
        <Typography variant="body2" color="text.disabled">
          {EM_DASH}
        </Typography>
      </TableCell>
    );
  }

  const segment = SEGMENTS.find((s) => s.key === segmentKey);
  const color = segmentKey === 'totalCycle' ? 'text.primary' : segment?.color;

  return (
    <TableCell align="right">
      <Typography
        variant="body2"
        sx={{
          color,
          fontWeight: segmentKey === 'totalCycle' ? 600 : 400,
        }}
      >
        {formatDays(value)}
      </Typography>
    </TableCell>
  );
}

interface CarryingCostCellProps {
  value: Decimal | null;
  breakdown: LifecycleCarryingCostBreakdown;
  currencyCode: IsoCurrencyCode;
}

// Segments shown in the breakdown tooltip, in the same order as the
// on-screen cards / Gantt. Date lag is omitted because it has no
// carrying-cost contribution (label gap, not a waiting interval).
const CARRYING_COST_BREAKDOWN_ORDER: {
  key: keyof LifecycleCarryingCostBreakdown;
  label: string;
}[] = [
  { key: 'chargeLag', label: 'Charge lag' },
  { key: 'invoiceReview', label: 'Invoice review' },
  { key: 'deliveryLag', label: 'Delivery lag' },
  { key: 'actualTerms', label: 'Actual terms' },
  { key: 'delinquency', label: 'Delinquency' },
];

function CarryingCostCell(props: CarryingCostCellProps) {
  const { value, breakdown, currencyCode } = props;
  if (value === null) {
    return (
      <TableCell align="right">
        <Typography variant="body2" color="text.disabled">
          {EM_DASH}
        </Typography>
      </TableCell>
    );
  }

  const tooltipBody = (
    <Stack spacing={0.5}>
      {CARRYING_COST_BREAKDOWN_ORDER.map(({ key, label }) => {
        const segmentCost = breakdown[key];
        return (
          <Box
            key={key}
            sx={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: 1 }}
          >
            <Typography variant="caption">{label}</Typography>
            <Typography
              variant="caption"
              sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
            >
              {formatAmountWithCurrency(segmentCost, currencyCode)}
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );

  // Negative values are carrying gains (the business held cash the supplier's
  // way or billed ahead) — surface them with success.dark so the table
  // reads at a glance.
  const isGain = value.isNegative();
  return (
    <TableCell align="right">
      <Tooltip title={tooltipBody} arrow placement="left">
        <Typography
          component="span"
          variant="body2"
          sx={{
            display: 'inline-block',
            fontWeight: 500,
            cursor: 'help',
            color: isGain ? 'success.dark' : undefined,
          }}
        >
          {formatAmountWithCurrency(value, currencyCode)}
        </Typography>
      </Tooltip>
    </TableCell>
  );
}
